import { Skeleton } from "@/components/ui";
import { useGetPatient } from "@/features/Patient/network/usePatient";
import { isUniversalClinicPatient } from "@/features/Patient/utils";
import { useConfigCatFeatureFlag } from "@/network/useConfigCatFeatureFlag";
import type { DiagnosticReport } from "@curai/js-fhir-tools";
import { LegacyResultsReviewForm } from "./LegacyResultsReviewForm";
import { UniversalClinicResultsReviewForm } from "./UniversalClinicResultsReviewForm";

/**
 * Entry point for the lab results review form. Decides which form to render:
 * async-labs Universal Clinic patients get the finalize-review flow, everyone
 * else gets the legacy triage flow.
 */
export const ResultsReviewForm = ({
  patientId,
  serviceRequestId,
  diagnosticReport,
}: {
  patientId: string;
  serviceRequestId: string;
  diagnosticReport?: DiagnosticReport;
}) => {
  const { data: patient, isLoading: isLoadingPatient } =
    useGetPatient(patientId);
  const { data: isLabResultSummaryUiEnabled, isLoading: isLoadingFlag } =
    useConfigCatFeatureFlag("lab-result-summary-ui");

  if (isLoadingPatient || isLoadingFlag) {
    return (
      <div className="grid gap-2">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-7 w-24 justify-self-end" />
      </div>
    );
  }

  if (isLabResultSummaryUiEnabled && isUniversalClinicPatient(patient)) {
    return (
      <UniversalClinicResultsReviewForm
        patientId={patientId}
        serviceRequestId={serviceRequestId}
        diagnosticReport={diagnosticReport}
        patient={patient}
      />
    );
  }

  return (
    <LegacyResultsReviewForm
      patientId={patientId}
      serviceRequestId={serviceRequestId}
      patient={patient}
    />
  );
};
